/**
 * Loading and driving the Turnstile widget.
 *
 * Kept out of `index.html` deliberately: this is a third-party script, and the
 * only places that need it are the name box on `/rsvp` and the dialog in front
 * of the room block on `/travel`. Injecting it here means the home page —
 * which is nearly all the traffic — never fetches it.
 *
 * Rendered explicitly rather than by scanning the document for a class. The
 * widget lives inside a component that mounts and unmounts as the route
 * changes, and auto-rendering has no idea about any of that; `render` returns
 * a handle, which is what makes `reset` and `remove` possible.
 */

const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type Turnstile = {
  render: (el: HTMLElement, options: Record<string, unknown>) => string | undefined;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

/** The site key, or '' when none is configured — see `ready`. */
export const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

/**
 * Whether to put a widget on the page at all.
 *
 * With no site key there is nothing to render, and the Worker will be doing
 * the matching thing at its end: refusing lookups outright on an https origin,
 * and allowing them with a warning on http. So an unconfigured local build
 * still has a working name box, and an unconfigured deployment has a visibly
 * broken one rather than a quietly unprotected one.
 */
export const enabled = SITE_KEY !== '';

let loading: Promise<Turnstile> | null = null;

/** Loads the script once, however many times this is called. */
export function load(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<Turnstile>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile loaded without defining itself.'));
    };
    script.onerror = () => {
      // Let a later attempt try again — a blocked request now may be a
      // extension or a flaky network rather than a permanent state.
      loading = null;
      reject(new Error('Turnstile failed to load.'));
    };
    document.head.append(script);
  });
  return loading;
}
